import { Component } from '@theme/component';

class NeobrutalistAnnouncementBar extends Component {
  #current = 0;
  #interval = null;
  #timerIntervals = [];

  connectedCallback() {
    super.connectedCallback();

    // 1. Storage check for close/dismiss state
    if (this.#isDismissed()) {
      this.style.display = 'none';
      return;
    }

    // 2. Initialize timer blocks
    this.#initCountdowns();

    // 3. Initialize slide cycle if layout is slider
    const layout = this.getAttribute('data-layout') || 'marquee';
    if (layout === 'slider') {
      this.#startSlider();
    }

    // 4. Attach delegated click listeners for buttons
    this.addEventListener('click', this.#handleClick.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopSlider();
    this.#clearCountdowns();
  }

  #handleClick(e) {
    // Check if Close Button was clicked
    const closeBtn = e.target.closest('.neobrutalist-announcement__close');
    if (closeBtn) {
      e.preventDefault();
      this.dismiss();
      return;
    }

    // Check if Copy Coupon Button was clicked
    const copyBtn = e.target.closest('.neobrutalist-discount-copy');
    if (copyBtn) {
      e.preventDefault();
      this.#copyCode(copyBtn);
    }
  }

  // Close functionality
  dismiss() {
    const expirationDays = parseInt(this.getAttribute('data-cookie-expiration') || '0', 10);
    const key = `neobrutalist-announcement-dismissed-${this.getAttribute('data-section-id') || 'default'}`;
    
    // Only store if expiration is > 0. If 0, it stays dismissed only until page reload (in memory).
    if (expirationDays > 0) {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + expirationDays);
      localStorage.setItem(key, expirationDate.toISOString());
    }

    // Smooth neobrutalist slide-up/out transition before hiding
    this.classList.add('announcement-bar--dismissing');
    this.addEventListener('transitionend', () => {
      this.style.display = 'none';
    }, { once: true });
  }

  #isDismissed() {
    const expirationDays = parseInt(this.getAttribute('data-cookie-expiration') || '0', 10);
    if (expirationDays <= 0) return false; // never permanently dismiss if 0

    const key = `neobrutalist-announcement-dismissed-${this.getAttribute('data-section-id') || 'default'}`;
    const localDismissed = localStorage.getItem(key);
    
    if (localDismissed) {
      const expirationDate = new Date(localDismissed);
      if (new Date() < expirationDate) {
        return true;
      } else {
        localStorage.removeItem(key);
      }
    }
    return false;
  }

  // Slider controls
  #startSlider() {
    const intervalSec = parseInt(this.getAttribute('data-slider-speed') || '5', 10);
    const slides = this.querySelectorAll('.neobrutalist-announcement__slide');
    if (slides.length <= 1) return;

    this.#current = 0;
    this.#showSlide(0);

    this.#interval = setInterval(() => {
      if (this.matches(':hover')) return; // pause on hover
      this.nextSlide();
    }, intervalSec * 1000);
  }

  #stopSlider() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }

  nextSlide() {
    const slides = this.querySelectorAll('.neobrutalist-announcement__slide');
    if (slides.length === 0) return;
    this.#current = (this.#current + 1) % slides.length;
    this.#showSlide(this.#current);
  }

  #showSlide(index) {
    const slides = this.querySelectorAll('.neobrutalist-announcement__slide');
    slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', `${i !== index}`);
      if (i === index) {
        slide.style.display = 'flex';
      } else {
        slide.style.display = 'none';
      }
    });
  }

  // Copy to clipboard coupon functionality
  #copyCode(btn) {
    const code = btn.getAttribute('data-code');
    if (!code) return;

    const performFeedback = () => {
      const originalText = btn.innerHTML;
      const copiedText = btn.getAttribute('data-copied-text') || 'Copied!';
      btn.innerHTML = `<span class="neobrutalist-copied-feedback">${copiedText}</span>`;
      btn.classList.add('neobrutalist-btn--copied');
      
      setTimeout(() => {
        btn.innerHTML = originalText;
        btn.classList.remove('neobrutalist-btn--copied');
      }, 2000);
    };

    // Modern API or Fallback
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(performFeedback).catch(err => {
        console.error('Failed to copy text: ', err);
      });
    } else {
      // Fallback for older browsers or HTTP
      const textArea = document.createElement("textarea");
      textArea.value = code;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        performFeedback();
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
      }
      textArea.remove();
    }
  }

  // Countdown timer blocks
  #initCountdowns() {
    // Instead of this.refs, we select directly because in marquee mode 
    // the markup might be duplicated many times and refs might behave oddly.
    const timers = this.querySelectorAll('.neobrutalist-timer-digits');
    
    timers.forEach((timerEl) => {
      let endTimeStr = timerEl.getAttribute('data-end-time');
      const expiredText = timerEl.getAttribute('data-expired-text') || 'Expired!';
      if (!endTimeStr) return;

      // Ensure proper formatting for Safari compatibility if possible
      endTimeStr = endTimeStr.replace(' ', 'T'); 

      const endTime = new Date(endTimeStr).getTime();
      if (isNaN(endTime)) {
        timerEl.textContent = 'Invalid date';
        return;
      }

      const updateTimer = () => {
        const now = new Date().getTime();
        const distance = endTime - now;

        if (distance <= 0) {
          timerEl.textContent = expiredText;
          timerEl.classList.add('timer--expired');
          return true; // expired
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24));
        const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((distance % (1000 * 60)) / 1000);

        let format = '';
        if (days > 0) {
          format += `${days}d `;
        }
        format += `${String(hours).padStart(2, '0')}h:${String(minutes).padStart(2, '0')}m:${String(seconds).padStart(2, '0')}s`;

        timerEl.textContent = format;
        return false;
      };

      // Run immediately
      const isExpired = updateTimer();
      if (!isExpired) {
        const intervalId = setInterval(() => {
          const expired = updateTimer();
          if (expired) {
            clearInterval(intervalId);
          }
        }, 1000);
        this.#timerIntervals.push(intervalId);
      }
    });
  }

  #clearCountdowns() {
    this.#timerIntervals.forEach(clearInterval);
    this.#timerIntervals = [];
  }
}

if (!customElements.get('neobrutalist-announcement-bar')) {
  customElements.define('neobrutalist-announcement-bar', NeobrutalistAnnouncementBar);
}
